/**
 * @module mixin/mixins
 * @description 전역 Vue 믹스인 — 모든 컴포넌트에서 공통으로 사용하는
 * 포맷터, 라우트 추적, 그리드 유틸리티, 전화번호/날짜/통화 변환 메서드를 제공합니다.
 */
import axios from 'axios'
import store from '@/store'
// eslint-disable-next-line camelcase
import jwt_decode from 'jwt-decode'
import { mapGetters } from 'vuex'

axios.defaults.withCredentials = true

export default {
  data() {
    return {
      routeTo: [],
      routePath: ''
    }
  },
  computed: {
    ...mapGetters(['serviceType']),
    authToken() {
      return store.state.auth.user.id.authToken
    }
  },
  watch: {
    '$route' (to, from) {
      if (to.path) {
        this.routeTo = to
        this.routePath = to.path
      }
    }
  },
  methods: {
    /**
     * Vuex 스토어의 authToken을 JWT 디코드하여 페이로드 객체를 반환합니다.
     *
     * @returns {object} 디코드된 JWT 페이로드 (language, userType 등 포함)
     */
    $tokenDecode() {
      const token = store.state.auth.user.id.authToken
      const decodedToken = jwt_decode(token)
      return decodedToken
    },
    /**
     * 숫자 값을 지정한 통화 포맷 문자열로 변환합니다.
     *
     * @param {number|string} value - 포맷할 숫자 값
     * @param {string} [format='#,###'] - 포맷 패턴 (예: '#,###', '$#,###.##')
     * @returns {string|number} 포맷된 통화 문자열, value가 0이거나 null이면 0 반환
     */
    $currencyFormat (value, format = '#,###') {
      if (value === 0 || value == null) return 0

      let currency = format.substring(0, 1)
      if (currency === '$' || currency === '₩') {
        format = format.substring(1, format.length)
      } else {
        currency = ''
      }

      let groupingSeparator = ','
      let maxFractionDigits = 0
      let decimalSeparator = '.'
      if (format.indexOf('.') === -1) {
        groupingSeparator = ','
      } else {
        if (format.indexOf(',') < format.indexOf('.')) {
          groupingSeparator = ','
          decimalSeparator = '.'
          maxFractionDigits = format.length - format.indexOf('.') - 1
        } else {
          groupingSeparator = '.'
          decimalSeparator = ','
          maxFractionDigits = format.length - format.indexOf(',') - 1
        }
      }

      let prefix = ''
      let d = ''
      // v = String(parseFloat(value).toFixed(maxFractionDigits));

      let dec = 1
      for (let i = 0; i < maxFractionDigits; i++) {
        dec = dec * 10
      }

      let v = String(Math.round(parseFloat(value) * dec) / dec)

      if (v.indexOf('-') > -1) {
        prefix = '-'
        v = v.substring(1)
      }

      if (maxFractionDigits > 0 && format.substring(format.length - 1, format.length) === '0') {
        v = String(parseFloat(v).toFixed(maxFractionDigits))
      }

      if (maxFractionDigits > 0 && v.indexOf('.') > -1) {
        d = v.substring(v.indexOf('.'))
        d = d.replace('.', decimalSeparator)
        v = v.substring(0, v.indexOf('.'))
      }
      const regExp = /\D/g
      v = v.replace(regExp, '')
      const r = /(\d+)(\d{3})/
      while (r.test(v)) {
        v = v.replace(r, '$1' + groupingSeparator + '$2')
      }

      return prefix + currency + String(v) + String(d)
    },
    /**
     * 전화번호 문자열을 하이픈 포함 형식으로 변환합니다.
     * 지역번호(02), 휴대폰(010~019), 8자리 상용 번호 등을 지원합니다.
     *
     * @param {string} num - 변환할 전화번호 문자열 (하이픈 포함·미포함 모두 허용)
     * @returns {string} 하이픈이 삽입된 전화번호 문자열
     */
    $phoneFormatter(num) {
      let formatNum = ''
      if (!(num === '' || num === 'undefined' || num === 'null' || num === null)) {
        num = num.replaceAll('-', '')
        formatNum = num
        if (num.length === 11) {
          formatNum = num.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
        } else if (num.length === 8) {
          formatNum = num.replace(/(\d{4})(\d{4})/, '$1-$2')
        } else {
          if (num.indexOf('02') === 0) {
            if (num.length === 9) {
              formatNum = num.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3')
            } else {
              formatNum = num.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3')
            }
          } else {
            if (num.length === 10) {
              formatNum = num.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
            } else {
              formatNum = num.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
            }
          }
        }
      }
      return formatNum
    },
    /**
     * 전화번호에서 하이픈을 모두 제거하여 숫자만 반환합니다.
     *
     * @param {string} num - 하이픈이 포함된 전화번호 문자열
     * @returns {string} 하이픈이 제거된 전화번호 문자열
     */
    $phoneFormatterHyphen(num) {
      let formatNum = ''
      if (!(num === '' || num === 'undefined' || num === 'null' || num === null)) {
        num = num.replaceAll('-', '')
        formatNum = num
      }
      return formatNum
    },
    /**
     * 검색 폼의 필드를 초기화합니다.
     * ADMIN이 아닌 경우 userGroupId 필드는 초기화하지 않으며,
     * partnerId 필드는 항상 유지합니다.
     */
    $fnSearchReset() {
      for (const key in this.search) {
        if (key !== 'partnerId') {
          if (store.state.auth.user.id.userType === 'ADMIN' || key !== 'userGroupId') {
            this.search[key] = ''
          }
        }
      }
    },
    /**
     * 숫자에 천 단위 쉼표를 삽입하여 문자열로 반환합니다.
     *
     * @param {number|string} number - 포맷할 숫자 또는 숫자 문자열
     * @param {Event} [event] - (미사용) 이벤트 객체
     * @returns {string|number} 천 단위 쉼표가 포함된 문자열, 0이면 0 반환
     */
    $formatNumber(number, event) {
      if (number === 0 || number === '0') {
        return number
      } else if (number === null) {
        return 0
      } else {
        const beforeNumber = number.toString().replace(/,/g, '')
        const result = beforeNumber.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        return result
      }
    },
    /**
     * 쉼표가 포함된 숫자 문자열에서 쉼표를 제거하여 순수 숫자로 반환합니다.
     *
     * @param {string|number} number - 쉼표가 포함된 숫자 문자열 또는 숫자
     * @returns {string|number} 쉼표가 제거된 숫자 또는 원본 숫자
     */
    $disFormatNumber(number) {
      if (number === 0 || number === '0') {
        return number
      } else if (number === '') {
        return 0
      } else if (typeof number === 'number') {
        return number
      } else {
        return number.replace(/,/g, '')
      }
    },
    /**
     * 접수 처리 유형 코드를 사람이 읽을 수 있는 한글 문자열로 변환합니다.
     *
     * @param {string} value - 유형 코드 ('CALL_COMPLETE' | 'VISIT_REQUIRED' | '')
     * @returns {string} 변환된 한글 텍스트
     */
    $requestTypeFormatter(value) {
      let formatValue = ''
      if (value === 'CALL_COMPLETE') {
        formatValue = '전화 문의 완료'
      } else if (value === 'VISIT_REQUIRED') {
        formatValue = '현장 방문 필요'
      } else if (value === '') {
        formatValue = '-'
      }

      return formatValue
    },
    /**
     * 접수 상태 코드를 한글 상태 문자열로 변환합니다.
     *
     * @param {string} value - 상태 코드 (예: 'R_REQUEST', 'R_COMPLETE' 등)
     * @returns {string} 변환된 한글 상태 텍스트
     */
    $requestStatusFormatter(value) {
      let formatValue = ''
      if (value === 'R_REQUEST') {
        formatValue = '접수'
      } else if (value === 'R_ASSIGNMENT') {
        formatValue = '배정'
      } else if (value === 'R_VISIT') {
        formatValue = '방문예정'
      } else if (value === 'R_REPAIR') {
        formatValue = '수리중'
      } else if (value === 'R_DELAY') {
        formatValue = '지연'
      } else if (value === 'R_REVISIT') {
        formatValue = '재방문예정'
      } else if (value === 'R_COMPLETE') {
        formatValue = '완료'
      } else if (value === 'R_CONFIRM') {
        formatValue = '확정'
      } else if (value === '') {
        formatValue = '-'
      }

      return formatValue
    },
    /**
     * 상태 텍스트에 대응하는 CSS 클래스명을 반환합니다.
     *
     * @param {string} value - 상태 한글 텍스트 (예: '접수', '완료', '상담진행' 등)
     * @returns {string} CSS 클래스명 문자열
     */
    $classInsert(value) {
      let className = ''
      if (value === '접수') {
        className = 'status-receipt'
      } else if (value === '배정') {
        className = 'status-assignment'
      } else if (value === '방문예정') {
        className = 'status-expected'
      } else if (value === '방문확정대기') {
        className = 'status-waiting'
      } else if (value === '수리중') {
        className = 'status-repair'
      } else if (value === '재방문예정') {
        className = 'status-revisit'
      } else if (value === '완료') {
        className = 'status-complete'
      } else if (value === '확정') {
        className = 'status-confirm'
      } else if (value === '상담진행') {
        className = 'consultation-received'
      } else if (value === '유선완료' || value === '유선상완료') {
        className = 'consultation_call_completed'
      } else if (value === '수리접수' || value === '수리접수완료') {
        className = 'moved_to_repair_request'
      } else if (value === '상담취소' || value === '취소') {
        className = 'consultation_cancelled'
      } else if (value === '') {
        className = '-'
      }
      return className
    },
    /**
     * 문자열에서 첫 번째 연속 숫자 부분만 추출합니다.
     *
     * @param {string} value - 숫자가 포함된 문자열
     * @returns {string} 추출된 숫자 문자열, 숫자가 없으면 빈 문자열 반환
     */
    $stringCut(value) {
      const match = value.match(/\d+/)

      return match ? match[0] : ''
    },
    /**
     * Date 객체 또는 'YYYY-MM-DD' 형식 문자열을 'YYYY-MM-DD' 문자열로 정규화합니다.
     *
     * @param {Date|string} date - 변환할 날짜 (Date 객체 또는 'YYYY-MM-DD' 문자열)
     * @returns {string} 'YYYY-MM-DD' 형식의 날짜 문자열
     */
    $formatDate(date) {
      const regex = /^\d{4}-\d{2}-\d{2}$/

      if (regex.test(date)) {
        return date
      } else {
        const year = date.getFullYear()
        const month = ('0' + (date.getMonth() + 1)).slice(-2)
        const day = ('0' + date.getDate()).slice(-2)
        return `${year}-${month}-${day}`
      }
    },
    /**
     * 'YYYYMMDD' 형식 문자열을 'YYYY-MM-DD' 형식으로 변환합니다.
     *
     * @param {string|null|undefined} date - 변환할 8자리 날짜 문자열
     * @returns {string|null|undefined} 'YYYY-MM-DD' 형식 문자열, 값이 없으면 원본 반환
     */
    $formatStringToDate(date) {
      if (!date) {
        return date
      } else {
        const year = date.slice(0, 4)
        const month = date.slice(4, 6)
        const day = date.slice(6, 8)
        return `${year}-${month}-${day}`
      }
    },
    /**
     * 'YYYY-MM-DD HH:mm:ss' 형식의 날짜-시간 문자열에서 날짜 부분만 추출합니다.
     *
     * @param {string} date - 'YYYY-MM-DD HH:mm:ss' 형식의 날짜-시간 문자열
     * @returns {string} 'YYYY-MM-DD' 날짜 문자열
     */
    $formatOnlyDate(date) {
      const dateTime = date
      const dateOnly = dateTime.split(' ')[0]

      return dateOnly
    },
    /**
     * FormData 객체의 키-값 쌍을 window.__APP_LOG__로 출력합니다.
     * Blob 타입의 값은 텍스트로 변환 후 출력합니다.
     *
     * @param {FormData} formData - 디버그 출력할 FormData 객체
     */
    $logFormData(formData) {
      for (const [key, value] of formData.entries()) {
        if (value instanceof Blob) {
          value.text().then(text => window.__APP_LOG__(key, text))
        } else {
          window.__APP_LOG__(key, value)
        }
      }
    },
    /**
     * 입력된 전화번호가 유효한 형식인지 검증합니다.
     * 유효하지 않으면 appAlert를 표시하고 해당 입력 필드에 포커스를 줍니다.
     * 핸드폰 번호, 지역 번호(02 포함), 상용 번호(예: 1577-1577) 형식을 지원합니다.
     *
     * @param {string} phoneNumber - 검증할 전화번호 문자열
     * @returns {boolean} 유효한 번호면 true, 유효하지 않으면 false
     */
    $checkPhoneNumber(phoneNumber) {
      const landlinePattern = /^(02|031|032|033|041|042|043|044|051|052|053|054|055|061|062|063|064|070)-\d{3,4}-\d{4}$/
      const mobilePattern = /^(010|011|016|017|018|019)-\d{3,4}-\d{4}$/
      const commercialPattern = /^\d{4}-\d{4}$/
      if (!phoneNumber ||
          (!landlinePattern.test(phoneNumber) && !mobilePattern.test(phoneNumber) && !commercialPattern.test(phoneNumber))) {
        window.appAlert(`${this.$t('register.customer')} ${this.$t('register.tel')}${this.$t('message.phoneNumberNotCorrect')}`)
        this.$refs.phoneNumberInput?.focus()
        return false
      }
      return true
    },
    /**
     * AG Grid의 모든 컬럼을 콘텐츠에 맞게 자동 리사이즈합니다.
     * onGridReady 에서 저장한 this.gridApi 를 쓴다. ag-grid-vue3 32 는 rowData 변경을
     * setTimeout(0) 뒤에 그리드에 반영하므로 nextTick 다음 태스크에서 맞춘다.
     */
    $gridAllColumnAutoSize() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.gridApi) {
            this.gridApi.autoSizeAllColumns()
          }
        })
      })
    },
    /**
     * AG Grid의 customerName 컬럼을 콘텐츠에 맞게 자동 리사이즈합니다.
     */
    $gridCustomerColumnAutoSize() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.gridApi) {
            this.gridApi.autoSizeColumns(['customerName'])
          }
        })
      })
    },
    /**
     * AG Grid의 상담 관련 주요 컬럼들(partnerName, productModelName, serialNumber, userName)을
     * 콘텐츠에 맞게 자동 리사이즈합니다.
     */
    $gridConsultationColumnAutoSize() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.gridApi) {
            this.gridApi.autoSizeColumns(['partnerName', 'productModelName', 'serialNumber', 'userName'])
          }
        })
      })
    },
    /**
     * 문자열이 숫자로만 구성되어 있는지 검증합니다.
     *
     * @param {string} number - 검증할 문자열
     * @returns {boolean} 숫자로만 구성된 경우 true
     */
    $testNumber(number) {
      const regex = /^[0-9]+$/
      return regex.test(number)
    },
    /**
     * 숫자를 소수점 두 자리로 포맷합니다.
     * 0이거나 100이거나 number 타입이 아닌 경우 원본을 반환합니다.
     *
     * @param {number} decimal - 포맷할 숫자
     * @returns {string|number} 소수점 두 자리 문자열 또는 원본 값
     */
    $formatTwoDecimalPlaces(decimal) {
      if (decimal !== 0 && typeof decimal === 'number' && decimal !== 100) {
        return decimal.toFixed(2)
      } else {
        return decimal
      }
    }
  }
}

/**
 * 객체를 URL 쿼리 문자열로 변환합니다.
 *
 * @param {object} params - 쿼리 파라미터 객체
 * @returns {string} '?key=value&...' 형식의 쿼리 문자열, 파라미터가 없으면 빈 문자열 반환
 */
export function buildQueryString(params) {
  const query = new URLSearchParams(params).toString()
  return query ? `?${query}` : ''
}

/**
 * Vuex 스토어의 authToken을 JWT 디코드하여 페이로드 객체를 반환합니다.
 * 컴포넌트 외부(일반 JS 함수 컨텍스트)에서 사용할 수 있는 독립형 버전입니다.
 *
 * @param {any} [params] - (미사용) 예약 파라미터
 * @returns {object} 디코드된 JWT 페이로드
 */
export function tokenDecode(params) {
  const token = store.state.auth.user.id.authToken
  const decodedToken = jwt_decode(token)
  return decodedToken
}
